/**
 * @module CreateNotificationPush
 * @package @geenius/notifications-solidjs-css
 * @description Manages browser notification permissions and push subscription
 * requests for the SolidJS vanilla CSS notifications variant.
 */

import type { Accessor } from 'solid-js'
import { createSignal } from 'solid-js'

/**
 * Push subscription state returned by {@link createNotificationPush}.
 */
export interface CreateNotificationPushResult {
  permission: Accessor<NotificationPermission>
  subscription: Accessor<PushSubscription | null>
  requestPermission: () => Promise<NotificationPermission>
  subscribe: (vapidPublicKey?: string) => Promise<PushSubscription | null>
}

/**
 * Requests browser push permission and creates subscriptions when supported.
 *
 * @returns Current permission state plus permission/subscription helpers.
 */
export function createNotificationPush(): CreateNotificationPushResult {
  const [permission, setPermission] = createSignal<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default',
  )
  const [subscription, setSubscription] = createSignal<PushSubscription | null>(null)

  const requestPermission = async (): Promise<NotificationPermission> => {
    if (typeof Notification === 'undefined') {
      return 'denied'
    }

    const result = await Notification.requestPermission()
    setPermission(result)
    return result
  }

  const subscribe = async (
    vapidPublicKey?: string,
  ): Promise<PushSubscription | null> => {
    if (!('serviceWorker' in navigator) || !vapidPublicKey) {
      return null
    }

    const registration = await navigator.serviceWorker.ready
    const nextSubscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidPublicKey,
    })

    setSubscription(nextSubscription)
    return nextSubscription
  }

  return { permission, subscription, requestPermission, subscribe }
}
